/*
---------------------------------------------------------
Copyright © 2026 Daniel Woik (DW E-Commerce). All rights reserved.
Custom-coded by DW E-Commerce. This code is licensed for use on
this store only, as part of the DW E-Commerce optimization service.
Copying, reselling, redistributing or using it on any other store
is not permitted without written permission.
---------------------------------------------------------
DW Kit:   Cart Drawer
Version:  1.9.0
Updated:  2026-10-14 — Mouse drag scrolls the empty-cart category row and never counts as a click; an empty installments block takes no space.
Source:   DW-Component-Library/Kits/Cart-Drawer

FUNCTION LOCKED. Install unchanged. No theme code is imported; only
Shopify's standard Ajax Cart API and Section Rendering API are used.

Public API for theme wiring (one line per theme, never a rewrite):
  window.DWCart.open()      open the drawer
  window.DWCart.close()     close it
  window.DWCart.refresh()   re-render after a change made elsewhere
Events on document:
  dw:cart:updated   detail: { cart }   after every change (update count bubbles here)
  dw:cart:open / dw:cart:close
---------------------------------------------------------
*/
(function () {
  if (window.DWCart) return;

  var OWN_HEADER = 'X-DW-Cart';
  var nativeFetch = window.fetch.bind(window);

  function drawer() {
    return document.getElementById('dw-cart-drawer');
  }

  /* The section id is dynamic when the drawer sits in a section group. */
  function sectionId() {
    var el = drawer();
    return (el && el.dataset.sectionId) || 'dw-cart-drawer';
  }

  function routes() {
    var el = drawer();
    var root = (el && el.dataset.rootUrl) || '/';
    if (root.slice(-1) !== '/') root += '/';
    return {
      cart: (el && el.dataset.cartUrl) || root + 'cart',
      add: (el && el.dataset.cartAddUrl) || root + 'cart/add',
      change: (el && el.dataset.cartChangeUrl) || root + 'cart/change',
      update: (el && el.dataset.cartUpdateUrl) || root + 'cart/update'
    };
  }

  function isCartUrl(url) {
    try {
      var path = new URL(url, window.location.origin).pathname;
      return /\/cart\/(add|change|update|clear)(\.js)?$/.test(path);
    } catch (e) {
      return false;
    }
  }

  function isAddUrl(url) {
    try {
      return /\/cart\/add(\.js)?$/.test(new URL(url, window.location.origin).pathname);
    } catch (e) {
      return false;
    }
  }

  /* ---------- Scroll lock without layout jump ---------- */
  var lockedScrollbar = 0;
  function lockScroll() {
    var html = document.documentElement;
    lockedScrollbar = window.innerWidth - html.clientWidth;
    html.classList.add('dw-cart-open');
    html.style.overflow = 'hidden';
    if (lockedScrollbar > 0) document.body.style.paddingRight = lockedScrollbar + 'px';
  }
  function unlockScroll() {
    var html = document.documentElement;
    html.classList.remove('dw-cart-open');
    html.style.overflow = '';
    document.body.style.paddingRight = '';
  }

  /* ---------- Desktop push mode and pin ---------- */
  var pushed = false;
  var PIN_KEY = 'dw-cart-pinned';

  function pushWanted() {
    var el = drawer();
    return !!el && el.getAttribute('data-desktop-mode') === 'push' && window.matchMedia('(min-width: 990px)').matches;
  }

  function setPushed(on) {
    var html = document.documentElement;
    var el = drawer();
    var panel = el && el.querySelector('.dw-cart__panel');
    pushed = on;
    if (on && panel) html.style.setProperty('--dw-cart-push-width', panel.getBoundingClientRect().width + 'px');
    html.classList.toggle('dw-cart-pushed', on);
    if (panel) panel.setAttribute('aria-modal', on ? 'false' : 'true');
  }

  function isPinned() {
    try { return window.localStorage.getItem(PIN_KEY) === '1'; } catch (e) { return false; }
  }

  function setPinned(on) {
    try {
      if (on) window.localStorage.setItem(PIN_KEY, '1');
      else window.localStorage.removeItem(PIN_KEY);
    } catch (e) {}
    syncPin();
  }

  function syncPin() {
    var el = drawer();
    var button = el && el.querySelector('[data-dw-cart-pin]');
    if (button) button.setAttribute('aria-pressed', isPinned() ? 'true' : 'false');
  }

  /* ---------- Open / close ---------- */
  var lastFocus = null;
  var isOpen = false;

  function open(options) {
    var noFocus = !!(options && options.noFocus);
    var el = drawer();
    if (!el || isOpen) return;
    isOpen = true;
    lastFocus = document.activeElement;
    if (pushWanted()) setPushed(true);
    else lockScroll();
    el.setAttribute('aria-hidden', 'false');
    el.setAttribute('open', '');
    var panel = el.querySelector('.dw-cart__panel');
    window.setTimeout(updateFade, 40);
    /* No focus move when the editor opens the drawer: it would take the keyboard away from the editor's text fields. */
    if (!noFocus) {
      window.setTimeout(function () {
        var close = el.querySelector('.dw-cart__close');
        (close || panel).focus({ preventScroll: true });
      }, 60);
    }
    document.dispatchEvent(new CustomEvent('dw:cart:open'));
  }

  function close() {
    var el = drawer();
    if (!el || !isOpen) return;
    isOpen = false;
    el.removeAttribute('open');
    el.setAttribute('aria-hidden', 'true');
    if (pushed) setPushed(false);
    else window.setTimeout(unlockScroll, 350);
    if (isPinned()) setPinned(false);
    if (lastFocus && typeof lastFocus.focus === 'function') lastFocus.focus({ preventScroll: true });
    document.dispatchEvent(new CustomEvent('dw:cart:close'));
  }

  /* ---------- Scroll fade: shows that more items are above or below ---------- */
  var fadeObserver = null;

  function updateFade() {
    var el = drawer();
    var middle = el && el.querySelector('[data-dw-cart-scroll]');
    if (!middle) return;
    var max = middle.scrollHeight - middle.clientHeight;
    middle.classList.toggle('dw-has-more-above', middle.scrollTop > 2);
    middle.classList.toggle('dw-has-more-below', max - middle.scrollTop > 2);
  }

  function bindFade() {
    var el = drawer();
    var middle = el && el.querySelector('[data-dw-cart-scroll]');
    if (!middle) return;
    middle.addEventListener('scroll', updateFade, { passive: true });
    if ('ResizeObserver' in window) {
      if (fadeObserver) fadeObserver.disconnect();
      fadeObserver = new ResizeObserver(updateFade);
      fadeObserver.observe(middle);
      Array.prototype.forEach.call(middle.children, function (child) { fadeObserver.observe(child); });
    }
    updateFade();
  }

  /* ---------- Rendering ---------- */
  function showError(message) {
    var el = drawer();
    if (!el) return;
    var box = el.querySelector('[data-dw-cart-error]');
    if (!box) return;
    box.textContent = message || '';
    box.hidden = !message;
  }

  function applySectionHtml(html) {
    var el = drawer();
    if (!el || !html) return;
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var fresh = doc.querySelector('[data-dw-cart-content]');
    var current = el.querySelector('[data-dw-cart-content]');
    if (!fresh || !current) return;

    var scroller = current.querySelector('[data-dw-cart-scroll]');
    var scrollTop = scroller ? scroller.scrollTop : 0;

    /* Keep the recommendations element when it would show the same products, so it does not reload and flicker. */
    var oldRecs = current.querySelector('dw-cart-recommendations');
    var newRecs = fresh.querySelector('dw-cart-recommendations');
    if (oldRecs && newRecs && oldRecs.dataset.productId === newRecs.dataset.productId) {
      newRecs.replaceWith(oldRecs);
    }

    current.replaceWith(fresh);
    var newScroller = fresh.querySelector('[data-dw-cart-scroll]');
    if (newScroller) newScroller.scrollTop = scrollTop;
    bindFade();
    bindInstallments();
    syncPin();
    prepareEditorLinks();

    /* Inline scripts in rendered snippets do not run after innerHTML; re-run them. */
    fresh.querySelectorAll('script').forEach(function (old) {
      if (old.type && old.type !== 'text/javascript' && old.type !== 'module') return;
      var s = document.createElement('script');
      Array.prototype.forEach.call(old.attributes, function (a) { s.setAttribute(a.name, a.value); });
      s.textContent = old.textContent;
      old.replaceWith(s);
    });
  }

  function fetchCart() {
    return nativeFetch(routes().cart + '.js', { credentials: 'same-origin', cache: 'no-store' }).then(function (r) {
      return r.json();
    });
  }

  function announce(cart) {
    document.querySelectorAll('[data-dw-cart-count]').forEach(function (node) {
      node.textContent = cart.item_count;
      node.hidden = cart.item_count === 0;
    });
    document.dispatchEvent(new CustomEvent('dw:cart:updated', { detail: { cart: cart } }));
  }

  function refresh(options) {
    options = options || {};
    var url = window.location.pathname + '?sections=' + sectionId();
    return nativeFetch(url, { credentials: 'same-origin', cache: 'no-store', headers: { [OWN_HEADER]: '1' } })
      .then(function (r) { return r.json(); })
      .then(function (sections) {
        applySectionHtml(sections[sectionId()]);
        return fetchCart();
      })
      .then(function (cart) {
        announce(cart);
        if (options.open) open();
        return cart;
      });
  }

  /* One JSON request that also returns the drawer HTML. */
  function cartRequest(url, body) {
    var el = drawer();
    if (el) el.classList.add('dw-is-busy');
    showError('');
    body.sections = sectionId();
    body.sections_url = window.location.pathname;
    return nativeFetch(url + '.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', [OWN_HEADER]: '1' },
      body: JSON.stringify(body)
    })
      .then(function (r) {
        return r.json().then(function (data) {
          if (!r.ok || data.status) throw data;
          return data;
        });
      })
      .then(function (data) {
        if (data.sections) applySectionHtml(data.sections[sectionId()]);
        return fetchCart();
      })
      .then(function (cart) {
        announce(cart);
        return cart;
      })
      .finally(function () {
        if (el) el.classList.remove('dw-is-busy');
      });
  }

  function changeLine(line, quantity, row) {
    if (row) row.classList.add('dw-is-loading');
    return cartRequest(routes().change, { line: line, quantity: quantity }).catch(function (err) {
      if (row) row.classList.remove('dw-is-loading');
      var msg = (err && (err.description || err.message)) || 'Something went wrong. Please try again.';
      var box = row && row.querySelector('[data-dw-cart-line-error]');
      if (box) {
        box.textContent = msg;
        box.hidden = false;
      } else {
        showError(msg);
      }
      refresh();
    });
  }

  /* Codes come from the cart itself, so a code can be removed from any pill (field, summary row or product line). */
  function currentCodes() {
    return fetchCart().then(function (cart) {
      return (cart.discount_codes || []).map(function (d) { return d.code; });
    }).catch(function () { return []; });
  }

  function applyDiscount(code) {
    code = (code || '').trim();
    if (!code) return;
    currentCodes().then(function (existing) {
      var codes = existing.filter(function (c) { return c.toLowerCase() !== code.toLowerCase(); });
      codes.push(code);
      return cartRequest(routes().update, { discount: codes.join(',') });
    })
      .then(function (cart) {
        var found = (cart.discount_codes || []).find(function (d) { return d.code.toLowerCase() === code.toLowerCase(); });
        if (!found) showError('This discount code is not valid.');
      })
      .catch(function () { showError('This discount code could not be applied.'); });
  }

  function removeDiscount(code) {
    var lower = String(code || '').toLowerCase();
    currentCodes().then(function (existing) {
      var codes = existing.filter(function (c) { return c.toLowerCase() !== lower; });
      return cartRequest(routes().update, { discount: codes.join(',') });
    }).catch(function () { refresh(); });
  }

  var noteTimer = null;
  function saveNote(value) {
    window.clearTimeout(noteTimer);
    noteTimer = window.setTimeout(function () {
      nativeFetch(routes().update + '.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', [OWN_HEADER]: '1' },
        body: JSON.stringify({ note: value })
      });
    }, 400);
  }

  /* ---------- Add to cart from any product form ---------- */
  function addFromForm(form, submitter) {
    var data = new FormData(form);
    if (submitter && submitter.name) data.append(submitter.name, submitter.value);
    data.append('sections', sectionId());
    data.append('sections_url', window.location.pathname);
    var button = submitter || form.querySelector('[type="submit"], button[name="add"]');
    if (button) {
      button.setAttribute('aria-busy', 'true');
      button.classList.add('dw-is-loading');
    }
    return nativeFetch(routes().add + '.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest', [OWN_HEADER]: '1' },
      body: data
    })
      .then(function (r) {
        return r.json().then(function (json) {
          if (!r.ok || json.status) throw json;
          return json;
        });
      })
      .then(function (json) {
        if (json.sections) applySectionHtml(json.sections[sectionId()]);
        return fetchCart();
      })
      .then(function (cart) {
        announce(cart);
        if (drawer() && drawer().dataset.autoOpen !== 'false') open();
      })
      .catch(function (err) {
        refresh().then(function () {
          showError((err && (err.description || err.message)) || 'This product could not be added.');
          open();
        });
      })
      .finally(function () {
        if (button) {
          button.removeAttribute('aria-busy');
          button.classList.remove('dw-is-loading');
        }
      });
  }

  /* ---------- Changes made by theme code (quick add, sticky bars, apps) ---------- */
  var pending = null;
  function scheduleRefresh(openAfter) {
    window.clearTimeout(pending);
    pending = window.setTimeout(function () {
      refresh({ open: openAfter && drawer() && drawer().dataset.autoOpen !== 'false' });
    }, 80);
  }

  window.fetch = function (input, init) {
    var url = typeof input === 'string' ? input : (input && input.url) || '';
    var headers = (init && init.headers) || (input && input.headers);
    var own = false;
    try {
      own = headers && (headers instanceof Headers ? headers.has(OWN_HEADER) : !!headers[OWN_HEADER]);
    } catch (e) {}
    var promise = nativeFetch(input, init);
    if (!own && isCartUrl(url)) {
      promise.then(function (r) {
        if (r.ok) scheduleRefresh(isAddUrl(url));
      });
    }
    return promise;
  };

  var nativeOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url) {
    if (isCartUrl(url)) {
      var add = isAddUrl(url);
      this.addEventListener('load', function () {
        if (this.status >= 200 && this.status < 300) scheduleRefresh(add);
      });
    }
    return nativeOpen.apply(this, arguments);
  };

  /* ---------- Event wiring ---------- */
  document.addEventListener(
    'submit',
    function (event) {
      var form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      var action = form.getAttribute('action') || '';
      if (!/\/cart\/add/.test(action)) return;
      if (!drawer()) return;
      /* Bubble phase: the theme's own form handler runs first. If it handled the add (its fetch is caught
         below and refreshes the drawer), do nothing. Only plain forms without theme JS are added here. Never add twice. */
      if (event.defaultPrevented) return;
      event.preventDefault();
      addFromForm(form, event.submitter);
    },
    false
  );

  /* Cart icon or any link to the cart page opens the drawer.
     Window + capture phase: runs before theme scripts and before the theme editor's own link handling. */
  window.addEventListener(
    'click',
    function (event) {
      var target = event.target;
      if (!(target instanceof Element)) return;
      var el = drawer();
      if (!el) return;
      var opener = target.closest('[data-dw-cart-open], a[href]');
      if (!opener || el.contains(opener)) return;
      var isOpener = opener.hasAttribute('data-dw-cart-open');
      if (!isOpener && opener.tagName === 'A') {
        try {
          var u = new URL(opener.getAttribute('href'), window.location.origin);
          var cartPath = new URL(routes().cart, window.location.origin).pathname;
          isOpener = u.origin === window.location.origin && u.pathname.replace(/\/$/, '') === cartPath.replace(/\/$/, '') && !u.search;
        } catch (e) {}
      }
      if (!isOpener) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      open();
    },
    true
  );

  document.addEventListener(
    'click',
    function (event) {
      var target = event.target;
      if (!(target instanceof Element)) return;
      var el = drawer();
      if (!el) return;

      if (!el.contains(target)) return;

      if (target.closest('[data-dw-cart-close]')) {
        event.preventDefault();
        close();
        return;
      }

      var row = target.closest('[data-dw-cart-line]');
      var qtyButton = target.closest('[data-dw-cart-qty]');
      if (row && qtyButton) {
        var input = row.querySelector('[data-dw-cart-qty-input]');
        var next = Math.max(0, (parseInt(input.value, 10) || 0) + parseInt(qtyButton.getAttribute('data-dw-cart-qty'), 10));
        input.value = next;
        changeLine(parseInt(row.getAttribute('data-dw-cart-line'), 10), next, row);
        return;
      }

      if (row && target.closest('[data-dw-cart-remove]')) {
        changeLine(parseInt(row.getAttribute('data-dw-cart-line'), 10), 0, row);
        return;
      }

      if (target.closest('[data-dw-cart-discount-apply]')) {
        var field = el.querySelector('[data-dw-cart-discount-input]');
        applyDiscount(field && field.value);
        return;
      }

      var removeCode = target.closest('[data-dw-cart-discount-remove]');
      if (removeCode) {
        var codeEl = removeCode.closest('[data-code]');
        if (codeEl) removeDiscount(codeEl.getAttribute('data-code'));
      }
    },
    true
  );

  document.addEventListener('change', function (event) {
    var input = event.target;
    if (!(input instanceof Element) || !input.matches('#dw-cart-drawer [data-dw-cart-qty-input]')) return;
    var row = input.closest('[data-dw-cart-line]');
    var qty = Math.max(0, parseInt(input.value, 10) || 0);
    changeLine(parseInt(row.getAttribute('data-dw-cart-line'), 10), qty, row);
  });

  document.addEventListener('input', function (event) {
    var input = event.target;
    if (input instanceof Element && input.matches('#dw-cart-drawer [data-dw-cart-note]')) saveNote(input.value);
  });

  document.addEventListener('keydown', function (event) {
    var el = drawer();
    if (!el || !isOpen) return;
    if (event.key === 'Escape') {
      event.stopImmediatePropagation();
      close();
      return;
    }
    if (event.key === 'Enter' && event.target instanceof Element && event.target.matches('[data-dw-cart-discount-input]')) {
      event.preventDefault();
      applyDiscount(event.target.value);
      return;
    }
    if (event.key !== 'Tab' || pushed) return;
    var focusable = Array.prototype.filter.call(
      el.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), textarea, select, summary, [tabindex]:not([tabindex="-1"])'),
      function (n) { return n.offsetParent !== null; }
    );
    if (!focusable.length) return;
    var first = focusable[0];
    var last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }, true);

  /* Theme editor only: the editor follows links itself, before any page script can stop it.
     Links to the cart page become buttons that open the drawer, so the drawer can be checked in the editor. */
  function prepareEditorLinks() {
    if (!(window.Shopify && window.Shopify.designMode)) return;
    var el = drawer();
    if (!el) return;
    var cartPath = new URL(routes().cart, window.location.origin).pathname.replace(/\/$/, '');
    document.querySelectorAll('a[href]').forEach(function (a) {
      if (el.contains(a)) return;
      try {
        var u = new URL(a.getAttribute('href'), window.location.origin);
        if (u.origin !== window.location.origin || u.search || u.pathname.replace(/\/$/, '') !== cartPath) return;
      } catch (e) { return; }
      a.setAttribute('data-dw-cart-open', '');
      a.setAttribute('data-dw-cart-href', a.getAttribute('href'));
      a.removeAttribute('href');
      a.setAttribute('role', 'button');
      a.setAttribute('tabindex', '0');
      a.style.cursor = 'pointer';
    });
  }

  /* ---------- Installments: Shopify's element stays empty when Shop Pay Installments is off
     or the total does not qualify. An empty block then takes no space (see .dw-is-collapsed). ---------- */
  var termsObserver = null;

  function bindInstallments() {
    var el = drawer();
    if (!el) return;
    var blocks = Array.prototype.slice.call(el.querySelectorAll('.dw-cart__block--installments'));
    if (termsObserver) { termsObserver.disconnect(); termsObserver = null; }
    if (!blocks.length) return;
    function check() {
      blocks.forEach(function (block) {
        var inner = block.querySelector('.dw-cart-installments');
        block.classList.toggle('dw-is-collapsed', !inner || inner.getBoundingClientRect().height < 1);
      });
    }
    check();
    if ('ResizeObserver' in window) {
      termsObserver = new ResizeObserver(check);
      blocks.forEach(function (block) {
        var inner = block.querySelector('.dw-cart-installments');
        if (inner) termsObserver.observe(inner);
      });
    }
  }

  /* ---------- Swipe rows (empty cart: categories): a mouse drag scrolls the row and never counts as a click.
     When everything fits there is nothing to drag, and a click opens the link normally. Touch keeps native swiping. ---------- */
  document.addEventListener('pointerdown', function (event) {
    if (event.pointerType !== 'mouse' || event.button !== 0) return;
    var target = event.target;
    if (!(target instanceof Element)) return;
    var el = drawer();
    var row = target.closest('.dw-cart__row');
    if (!el || !row || !el.contains(row)) return;
    if (row.scrollWidth - row.clientWidth < 2) return;

    var startX = event.clientX;
    var startScroll = row.scrollLeft;
    var moved = false;
    var controller = new AbortController();
    var signal = controller.signal;

    function preventClick(e) {
      e.preventDefault();
      e.stopPropagation();
    }

    function onMove(e) {
      var dx = e.clientX - startX;
      if (!moved) {
        if (Math.abs(dx) < 5) return;
        moved = true;
        row.classList.add('dw-is-dragging');
        document.addEventListener('click', preventClick, { capture: true, once: true });
      }
      row.scrollLeft = startScroll - dx;
    }

    function onUp() {
      controller.abort();
      row.classList.remove('dw-is-dragging');
      if (moved) {
        window.setTimeout(function () { document.removeEventListener('click', preventClick, { capture: true }); }, 0);
      }
    }

    document.addEventListener('pointermove', onMove, { signal: signal });
    document.addEventListener('pointerup', onUp, { signal: signal });
    document.addEventListener('pointercancel', onUp, { signal: signal });
  });

  document.addEventListener('dragstart', function (event) {
    var el = drawer();
    var target = event.target;
    if (el && target instanceof Element && el.contains(target) && target.closest('.dw-cart__row')) event.preventDefault();
  });

  /* ---------- Start ---------- */
  function init() {
    var el = drawer();
    if (!el) return;
    /* Enable transitions only after the first paint, so nothing slides on page load. */
    window.requestAnimationFrame(function () {
      window.requestAnimationFrame(function () { el.classList.add('dw-is-ready'); });
    });
    bindFade();
    bindInstallments();
    syncPin();
    prepareEditorLinks();
    window.addEventListener('resize', updateFade, { passive: true });
    window.requestAnimationFrame(function () {
      window.requestAnimationFrame(function () { document.documentElement.classList.add('dw-cart-push-ready'); });
    });

    /* Pinned in push mode: open again on every page and after a reload, without sliding in (also with an empty cart). */
    if (el.getAttribute('data-pin') === 'true' && isPinned() && pushWanted()) {
      open({ noFocus: true });
    }

    var params = new URLSearchParams(window.location.search);
    if (params.get('open_cart_drawer') === '1') {
      open();
      params.delete('open_cart_drawer');
      var q = params.toString();
      window.history.replaceState({}, '', window.location.pathname + (q ? '?' + q : '') + window.location.hash);
    }
  }

  /* Theme editor: open while the section is selected. */
  document.addEventListener('shopify:section:select', function (e) {
    if (e.detail && e.detail.sectionId === sectionId()) open({ noFocus: true });
  });
  document.addEventListener('shopify:section:deselect', function (e) {
    if (e.detail && e.detail.sectionId === sectionId()) close();
  });
  document.addEventListener('shopify:section:load', function () { prepareEditorLinks(); });
  document.addEventListener('shopify:section:load', function (e) {
    if (e.detail && e.detail.sectionId === sectionId()) { isOpen = false; init(); open({ noFocus: true }); }
  });

  document.addEventListener('shopify:block:select', function (e) {
    var el = drawer();
    if (el && e.target instanceof Element && el.contains(e.target)) open({ noFocus: true });
  });

  /* Back/forward cache: the cart may have changed in another tab. */
  window.addEventListener('pageshow', function (e) {
    if (e.persisted) refresh();
  });

  /* Pin button */
  document.addEventListener('click', function (event) {
    var target = event.target;
    if (!(target instanceof Element)) return;
    var button = target.closest('[data-dw-cart-pin]');
    if (!button) return;
    event.preventDefault();
    setPinned(!isPinned());
  });

  /* Crossing the 990px line while open: switch between push and cover. */
  window.addEventListener('resize', function () {
    if (!isOpen) return;
    var want = pushWanted();
    if (want && !pushed) { unlockScroll(); setPushed(true); }
    else if (!want && pushed) { setPushed(false); lockScroll(); }
    else if (pushed) setPushed(true);
  }, { passive: true });

  window.DWCart = { open: open, close: close, refresh: refresh };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
